interface QueuedTask {
  url: string;
  resolve: (bitmap: ImageBitmap) => void;
  reject: (error: Error) => void;
}

export class TilePrecacher {
  private readonly cache = new Map<string, ImageBitmap>();
  private activeFetches = 0;
  private readonly maxConcurrent: number;
  private queue: QueuedTask[] = [];

  constructor(maxConcurrent = 6) {
    this.maxConcurrent = maxConcurrent;
  }

  get(url: string): ImageBitmap | undefined {
    return this.cache.get(url);
  }

  has(url: string): boolean {
    return this.cache.has(url);
  }

  get size(): number {
    return this.cache.size;
  }

  async prefetchUrls(urls: string[], signal?: AbortSignal): Promise<void> {
    const uncached = urls.filter((u) => !this.cache.has(u));
    if (uncached.length === 0) return;

    const promises = uncached.map(
      (url) =>
        new Promise<void>((resolve) => {
          if (signal?.aborted) {
            resolve();
            return;
          }

          this.queue.push({
            url,
            resolve: (bitmap) => {
              this.cache.set(url, bitmap);
              resolve();
            },
            reject: () => {
              resolve();
            },
          });
          this.processQueue(signal);
        }),
    );

    await Promise.all(promises);
  }

  clear(): void {
    this.queue = [];
    this.cache.forEach((bitmap) => bitmap.close());
    this.cache.clear();
    this.activeFetches = 0;
  }

  private processQueue(signal?: AbortSignal): void {
    while (this.activeFetches < this.maxConcurrent && this.queue.length > 0) {
      const task = this.queue.shift()!;

      if (signal?.aborted) {
        task.reject(new DOMException('Aborted', 'AbortError'));
        continue;
      }

      this.activeFetches++;

      this.fetchTile(task.url, signal)
        .then((bitmap) => task.resolve(bitmap))
        .catch((err) => task.reject(err))
        .finally(() => {
          this.activeFetches--;
          this.processQueue(signal);
        });
    }
  }

  private async fetchTile(url: string, signal?: AbortSignal): Promise<ImageBitmap> {
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    return createImageBitmap(blob);
  }
}
